import * as classnames from "classnames";
import * as React from "react";

import {
  Icon,
  Button,
  PageSubTitle,
  ApplicantChallengeHeaderMenu,
  Overlay,
  ChallengeCountDownTimer,
  HeaderLogo,
  EnvTag,
  Msg,
  CliSupportBadge,
} from "..";
import { ApplicantChallengeModel } from "../../models";
import { formatDateTimeSeconds } from "../../services/date";
import { ChallengeStyle, ChallengeTakenBy } from "../../services/enums";
import InitialData from "../../services/initialData";

/**
 * Prop interface
 */
export interface ApplicantChallengeHeaderProps {
  headerLogoURL?: string;
  examName: string;
  challenge: ApplicantChallengeModel;
  hasSwitchLanguage?: boolean;
  hasSwitchFrontendTemplate?: boolean;
  hasVersionCommand?: boolean;
  hasMenu?: boolean;
  isChallengeInProgress?: boolean;
  isUsingVsCodeEditor?: boolean;
  onReset?: () => void;
  onSwitchProgrammingLanguage?: () => void;
  onSwitchFrontendTemplate?: () => void;
  onShowTestInformation?: () => void;
  onVersionCommand?: () => void;
  onInitializeRemote?: () => void;
  onUpdateEnv?: () => void;
  onSubmit?: () => void;
  onSwitchTakenBy: ({ takenBy }: { takenBy: ChallengeTakenBy }) => void;
  className?: string;
  updatedAt?: string;
  takenBy?: ChallengeTakenBy;
  isOpenAppealComment?: boolean;
  onShowAppealComment?: () => void;
}

/**
 * React Component
 */
const ApplicantChallengeHeader = (props: ApplicantChallengeHeaderProps) => {
  const {
    headerLogoURL,
    examName,
    updatedAt,
    challenge,
    isChallengeInProgress = false,
    hasMenu = false,
    hasSwitchLanguage = false,
    hasSwitchFrontendTemplate = false,
    hasVersionCommand = false,
    onSubmit,
    onReset,
    onSwitchProgrammingLanguage,
    onSwitchFrontendTemplate,
    onVersionCommand,
    onShowTestInformation,
    onInitializeRemote,
    onUpdateEnv,
    onSwitchTakenBy,
    takenBy,
    className,
    isOpenAppealComment = false,
    onShowAppealComment,
    isUsingVsCodeEditor,
  } = props;

  const rootStyle = classnames(
    "code-applicant-challenge-header",
    "navbar",
    "is-mobile",
    {
      [`${className}`]: Boolean(className),
    },
  );

  const [isOpen, setIsOpen] = React.useState(false);

  const challengeTimeLeft = isChallengeInProgress && (
    <div className="code-applicant-challenge-header__timeleft">
      <div>
        <Icon type={"clock-o"} />
        <ChallengeCountDownTimer challengeId={challenge.id} />
      </div>
      {updatedAt && (
        <div className="last-saved-at">
          {formatDateTimeSeconds(updatedAt)} Saved
        </div>
      )}
    </div>
  );

  const challengeButton = isChallengeInProgress && (
    <Button type={"primary"} onClick={onSubmit} ariaLabel="Submit Challenge">
      <Msg id={"button.submitChallenge"} />
    </Button>
  );

  const challengeMenu = hasMenu && isChallengeInProgress && (
    <div>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        outlined={true}
        shrink={true}
        ariaLabel="Settings Menu"
        ariaPressed={isOpen}
      >
        <Icon type={"cog"} size={"medium"} />
      </Button>
      <ApplicantChallengeHeaderMenu
        isOpen={isOpen}
        onReset={onReset}
        onSwitchProgrammingLanguage={onSwitchProgrammingLanguage}
        onSwitchFrontendTemplate={onSwitchFrontendTemplate}
        onVersionCommand={onVersionCommand}
        onShowTestInformation={onShowTestInformation}
        takenBy={takenBy}
        onSwitchTakenBy={onSwitchTakenBy}
        onInitializeRemote={onInitializeRemote}
        onUpdateEnv={onUpdateEnv}
        hasSwitchLanguage={hasSwitchLanguage}
        hasSwitchFrontendTemplate={hasSwitchFrontendTemplate}
        hasVersionCommand={hasVersionCommand}
        hasSwitchTakenBy={
          challenge.localExamEnabled ||
          takenBy === ChallengeTakenBy.LocalMachine
        }
        isUsingVsCodeEditor={isUsingVsCodeEditor}
      />
    </div>
  );

  const appealCommentButton = ChallengeStyle.hasAppealComment(
    challenge.style,
  ) &&
    isChallengeInProgress &&
    !isOpenAppealComment && (
      <div className="code-applicant-challenge-header__appeal-link">
        <Button
          role="button"
          aria-label="Open Appeal Comment"
          aria-pressed={isOpenAppealComment}
          onClick={() => onShowAppealComment?.()}
        >
          <Msg id="applicant-challenge-link.addAppeal" />
          <Icon type="comment" />
        </Button>
      </div>
    );

  return (
    <div className={rootStyle} aria-expanded={isOpen}>
      <div className="code-applicant-challenge-header__left">
        <HeaderLogo
          className="is-text-ellipsis"
          imageUrl={headerLogoURL}
          displayName={examName}
        />
      </div>
      <div className="code-applicant-challenge-header__title">
        <PageSubTitle className="is-text-ellipsis is-hidden-touch">
          {challenge.title}
        </PageSubTitle>
      </div>
      <div className="code-applicant-challenge-header__right">
        <EnvTag envString={InitialData.env} />
        {takenBy === ChallengeTakenBy.LocalMachine && (
          <CliSupportBadge isExam />
        )}
        {challengeTimeLeft}
        {appealCommentButton}
        {challengeButton}
        {challengeMenu}
      </div>
      <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
    </div>
  );
};

export default ApplicantChallengeHeader;
